import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import ScaleRunner from '../scales-engine/ScaleRunner';
import { fetchScaleFromSupabase } from '../lib/fetchScale'; 
import { scoreScale, saveReport, rateReport, localToday } from '../lib/cortivoxApi';
import { useAuth } from '../auth/AuthContext';
import { buildSummaryReport } from '../patient-records/buildSummaryReport';
import { CheckCircle2, Home,ClipboardList, AlertTriangle, Info, RotateCcw } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import DrawingChecklist from '../scales-engine/itemTypes/drawing/DrawingChecklist';
import DrawingChecklistPentagon from '../scales-engine/itemTypes/drawing/DrawingChecklistPentagon';
import { buildStroopReport, buildSdmtReport } from '../patient-records/buildSpeedReport';
import SpeedTestReport from '../patient-records/SpeedTestReport';

// ИСПРАВЛЕНО: в четвёртой группе было [0-9af]{4} без дефиса между a и f —
// класс символов допускал только 0-9, "a", "f", но не b/c/d/e. Большинство
// настоящих UUID содержат эти буквы в этой позиции, поэтому canSave почти
// всегда ложно давал false для реальных пациентов, и отчёт тихо уходил в
// публичное превью вместо сохранения — без единой видимой ошибки.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SPEED_SCALES = new Set(['stroop', 'sdmt', 'sdmt_v2']);

export default function TestRunnerPage() {
  const { scaleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const patientProfile = location.state?.patientProfile || { 
    id: 'guest_001', 
    name: 'Гость', 
    age: null, 
    educationYears: 12 
  };

  const { doctor } = useAuth();

  const [report, setReport] = useState(null);
  const [manualScores, setManualScores] = useState({});
  const [dynamicScore, setDynamicScore] = useState(0);
  const [lastRawAnswers, setLastRawAnswers] = useState({}); 
  const [lastInstructionRepeats, setLastInstructionRepeats] = useState({});
  const [reportId, setReportId] = useState(null);
  const [testDate, setTestDate] = useState(null);
  const [scoreError, setScoreError] = useState(null);

  const [scaleData, setScaleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const scaleName = scaleId ? scaleId.toUpperCase() : '';

  useEffect(() => {
    if (!scaleId) return;
    
    setLoading(true);
    setScaleData(null);

        fetchScaleFromSupabase(scaleId).then(data => {
      // NIHSS 1c: правильный ответ — реальный возраст пациента (из профиля,
      // как MoCA). В режиме «показать» правильный вариант ОБЯЗАТЕЛЬНО
      // присутствует среди кнопок: варианты генерируются вокруг возраста.
      if (scaleId === 'nihss' && Array.isArray(data.questions)) {
        console.log('[AGE] профиль для 1c:', { age: patientProfile?.age, birthDate: patientProfile?.birthDate, birth_date: patientProfile?.birth_date });
        let age = patientProfile?.age;
        if (!age) {
          const b = patientProfile?.birthDate || patientProfile?.birth_date;
          if (b) {
            const d = new Date(b);
            if (!isNaN(d)) age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
          }
        }
        const q1c = data.questions.find(q => q.id === '1c');
        if (age && q1c) {
          const ageStr = String(age);
          q1c.correctOption = ageStr;
          q1c.validAnswer = ageStr;
          q1c.options = [age - 5, Number(age), age + 5, age + 10]
            .filter(n => n > 0)
            .map(String)
            .sort((x, y) => Number(x) - Number(y));
        }
      }
      setScaleData(data);
      setLoading(false);
    });
  }, [scaleId]);

  const manualItemIds = scaleData
    ? scaleData.questions.filter(q => q.type === 'drawing' || q.isManualAction).map(q => q.id)
    : [];
  const canSave = doctor?.role === 'doctor' && UUID_RE.test(String(patientProfile?.id ?? ''));

  const showResult = (res, rawAnswers, instructionRepeats) => {
        if (SPEED_SCALES.has(scaleId)) {
      // Приоритет — серверному расчёту (score_result от API): он же пишется
      // в scale_reports, экран и база не могут разойтись. Клиентский расчёт —
      // только fallback для гостевого превью (scoreScale без сохранения).
      const serverStroop = scaleId === 'stroop' ? res?.scoreResult?.stroop : null;
      // Для врача серверный результат обязателен: молчаливое клиентское
      // «превью» вместо серверного расчёта — ошибка, а не штатный путь.
      if (canSave && scaleId === 'stroop' && !serverStroop) {
        setScoreError({
          message: 'Серверный расчёт Струпа не получен — отчёт не может быть показан как превью.',
          retry: () => runScoring(rawAnswers, {}, { initial: false, date: localToday(), instructionRepeats }),
        });
        return;
      }
      const speedReport = serverStroop
            ? { kind: 'speed', server: true, saved: Boolean(res?.id), scaleId, scaleName: 'Тест Струпа',
            patientName: patientProfile?.name || 'Гость', date: localToday(),
            algorithm: `сервер · ${res?.algorithmVersion || 'stroop'}`,
            raw: res.scoreResult.stroop }
        : (scaleId === 'stroop'
            ? buildStroopReport(rawAnswers, patientProfile, scaleId, scaleData)
            : buildSdmtReport(rawAnswers, patientProfile, scaleId));
           if (speedReport) {
        // Раньше здесь стояло безусловное saved = false: у SDMT врача отчёт сохраняется
        // (saveReport вернул id), а на экране было «Результат НЕ сохранён».
        if (!speedReport.server) speedReport.saved = Boolean(res?.id);
        // SDMT: отчёт собран на клиенте (buildSdmtReport), а форма и нормы приходят
        // с сервера (sdmt-web-2, score_result.norms) — передаём их в отчёт.
        if (scaleId !== 'stroop' && res?.scoreResult?.norms) speedReport.norms = res.scoreResult.norms;
        setReport({ speedReport }); setDynamicScore(0); return;
      }
    }    

    const summaryReport = buildSummaryReport(scaleName, res.scoreResult, rawAnswers, scaleData, patientProfile, instructionRepeats);
    summaryReport.isPendingManualReview = res.isPendingManualReview;
    setReport(summaryReport);
    setDynamicScore(typeof res.scoreResult === 'object' ? res.scoreResult.totalScore : res.scoreResult);
  };

  const runScoring = async (rawAnswers, manual, { initial, date, instructionRepeats }) => {
    setScoreError(null);
    console.info('[SCORING] path:', reportId ? 'rate' : (initial && canSave) ? 'save' : 'preview',
    { initial, canSave, role: doctor?.role, patientId: patientProfile?.id, reportId });
    try {
      let res;
      console.log('[SCORING] route', { reportId, initial, canSave,
      route: reportId ? 'rateReport' : (initial && canSave ? 'saveReport' : 'scoreScale') });
      if (reportId) {
        res = await rateReport(reportId, { manualScores: manual, manualItemIds });
      } else if (initial && canSave) {
        res = await saveReport({
          patientId: patientProfile.id, scaleId, rawAnswers, manualScores: manual, manualItemIds, today: date,
        });
        setReportId(res.id);
      } else {
        res = await scoreScale(scaleId, { rawAnswers, manualScores: manual, patientProfile, manualItemIds, today: date });
      }
      console.log('[SCORING] result', { hasScoreResult: !!res?.scoreResult,
      hasServerStroop: !!res?.scoreResult?.stroop, algorithmVersion: res?.algorithmVersion });
      showResult(res, rawAnswers, instructionRepeats);
    } catch (err) {
    if (err?.status === 404 && err?.code === 'scale_not_supported') {
        // Врачу — ошибка с повтором (серверный расчёт обязателен), гостю — превью.
        if (canSave) {
          setScoreError({
            message: 'Серверный расчёт этой шкалы недоступен. Результат не сохранён.',
            retry: () => runScoring(rawAnswers, manual, { initial, date, instructionRepeats }),
          });
          return;
        }
        const manualTotal = Object.values(manual).reduce((a, b) => a + b, 0);
        showResult({
          scoreResult: manualTotal,
          isPendingManualReview: manualItemIds.some(id => manual[id] === undefined),
        }, rawAnswers, instructionRepeats);
        return;
      }
      setScoreError({
        message: err?.message || 'Не удалось рассчитать результат',
        retry: () => runScoring(rawAnswers, manual, { initial, date, instructionRepeats }),
      });
    }
  };

  const handleComplete = async (data) => {
    if (!scaleData) return;
    const date = localToday();
    const instructionRepeats = data.instructionRepeats || {};
    setTestDate(date);
    setLastRawAnswers(data.rawAnswers);
    setLastInstructionRepeats(instructionRepeats);
    await runScoring(data.rawAnswers, manualScores, { initial: true, date, instructionRepeats });
  };   

  const handleManualScoreChange = async (id, points) => {
    const newManualScores = { ...manualScores, [id]: points };
    setManualScores(newManualScores);
    await runScoring(lastRawAnswers, newManualScores, { initial: false, date: testDate, instructionRepeats: lastInstructionRepeats });
  };

  const handleDrawingChecklistSubmit = async (itemId, checklist) => {
    const newManualScores = { ...manualScores, [itemId]: checklist };
    setManualScores(newManualScores);
    await runScoring(lastRawAnswers, newManualScores, { initial: false, date: testDate, instructionRepeats: lastInstructionRepeats });
  };

  const scoreErrorBanner = scoreError && (
    <div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4 flex items-center justify-between gap-3 shadow-sm">
      <p className="text-sm">Не удалось рассчитать результат: {scoreError.message}</p>
      {scoreError.retry && (
      <button onClick={scoreError.retry} className="rounded-xl bg-primary text-white px-4 py-2 text-sm font-semibold shrink-0">
        Повторить расчёт
        </button>
      )}
    </div>
  );

  const getRowColor = (isCorrect, needsManual, points) => {
    if (needsManual) return 'bg-blue-50 border-blue-200';
    if (isCorrect === null) return 'bg-yellow-50 border-yellow-200';
    if (isCorrect === true) return 'bg-green-50 border-green-200';
    if (points > 0) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusText = (item) => {
    if (item.needsManualScore) return null;
    if (item.points !== null && item.points !== undefined) {
      return `${item.points} балл.`;
    }
    if (item.isCorrect === true) return '1 балл.';
    if (item.isCorrect === false) return '0 балл.';
    return '⚡ Клиническая';
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="text-xl text-muted-foreground">Загрузка вопросов из базы данных...</p>
      </div>
    );
  }

  if (!scaleData) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-2xl text-muted-foreground">Шкала не найдена в базе данных</p>
        <button onClick={() => navigate('/patient')} className="bg-primary text-white px-6 py-3 rounded-xl">Вернуться назад</button>
      </div>
    );
  }

  if (scoreError && !report) {
    return <div className="min-h-screen bg-background p-8 max-w-3xl mx-auto">{scoreErrorBanner}</div>;
  }

  const totalInstructionRepeats = report?.detailedAnswers
      ? report.detailedAnswers.filter(Boolean).reduce((sum, item) => sum + (item.repeats || 0), 0)
    : 0;
  
    if (report?.speedReport) {
    return <SpeedTestReport report={report.speedReport} onExit={() => navigate('/patient')} />;
  }

  // Если отчет готов - показываем его
  if (report) {
    return (
      <div className="min-h-screen bg-background p-8 overflow-y-auto">
        <div className="max-w-6xl mx-auto flex flex-col gap-8">

          {scoreErrorBanner}

          {report.isPendingManualReview && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
              <AlertTriangle className="size-6 text-blue-500 shrink-0" />
              <div>
                <p className="font-bold text-sm">Предварительные данные</p>
                <p className="text-xs">AI-диагноз рассчитан без учета рисунков. Пожалуйста, оцените куб и часы в таблице ниже для получения точного результата.</p>
              </div>
            </div>
          )}
          
          {report.ageNote && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
              <Info className="size-6 text-yellow-500 shrink-0" />
              <p className="text-xs font-medium">{report.ageNote}</p>
        </div>
          )}

          <div className="bg-secondary/50 rounded-3xl p-8 shadow-xl border border-border flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col items-start gap-2">
              <h1 className="text-3xl font-bold text-primary">Выписка: {report.scaleName}</h1>
              <p className="text-muted-foreground">Пациент: {patientProfile.name} | Дата: {report.date}</p>
              {/* НОВОЕ: строка-сводка Exec Summary — собрана в buildSummaryReport.js
                  по шаблону из уже посчитанных полей, здесь только отображается. */}
              {report.execSummaryText && (
                <p className="text-sm text-primary/90 font-medium leading-relaxed max-w-2xl">
                  {report.execSummaryText}
                </p>
              )}
              {/* НОВОЕ: чипы по всем 7 доменам — полные названия, компактный ряд
                  вместо перечисления в прозе. */}
              {report.subScores && report.subScores.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {report.subScores.map((sub, idx) => (
                    <span
                      key={idx}
                      className="text-[11px] bg-secondary px-2 py-1 rounded-full text-primary font-medium"
                    >
                      {sub.domain}: {sub.score}/{sub.max}
                    </span>
                  ))}
                </div>
              )}
              {totalInstructionRepeats > 0 && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground/80">
                    <RotateCcw className="size-3.5" />
                    Инструкция повторялась {totalInstructionRepeats} раз(а) за тест — детали в таблице ниже.
                </p>
              )}
            </div>
            <div className="flex items-center gap-8">
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Балл</p>
                <p className="text-5xl font-bold text-accent">{dynamicScore}</p>
                {/* НОВОЕ: раскрытие поправки за образование — раньше было видно
                    только итоговое число. */}
                {report.rawScore !== null && report.rawScore !== undefined && report.rawScore !== dynamicScore && (
                  <p className="text-[10px] text-muted-foreground mt-1 whitespace-nowrap">
                    сырой {report.rawScore} + поправка = {dynamicScore}
                  </p>
                )}
              </div>
              <div className="text-center md:text-left">
                <p className="text-sm text-muted-foreground">Интерпретация</p>
                <p className="text-xl font-semibold text-primary">{report.interpretation}</p>
              </div>
            </div>
          </div>

          <div className="bg-secondary/50 rounded-3xl p-6 shadow-xl border border-border">
            <h2 className="text-xl font-semibold text-primary mb-4">Клиническая интерпретация (AI)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div className="flex flex-col gap-4">
                {/* НОВОЕ: карточка по бдительности (хлопки на «А») — commissions/
                    omissions уже считались на сервере, но нигде не отображались.
                    Текст паттерна появляется только когда он реально повлиял на
                    балл — одна ошибка ("isolated") или их отсутствие не попадают
                    в vigilanceSummaryText, но числа всё равно видны всегда. */}
                {report.vigilanceDetail && (report.vigilanceDetail.commissions > 0 || report.vigilanceDetail.omissions > 0) && (
                  <div className="bg-background border border-border rounded-2xl p-4">
                    <p className="text-sm font-medium text-muted-foreground mb-2">
                      Бдительность (хлопки на «А») — экспериментально
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-center mb-2">
                      <div className="bg-secondary rounded-lg py-2">
                        <p className="text-lg font-bold text-primary">{report.vigilanceDetail.commissions}</p>
                        <p className="text-[10px] text-muted-foreground">лишних хлопков</p>
                      </div>
                      <div className="bg-secondary rounded-lg py-2">
                        <p className="text-lg font-bold text-primary">{report.vigilanceDetail.omissions}</p>
                        <p className="text-[10px] text-muted-foreground">пропусков</p>
                      </div>
                    </div>
                    {report.vigilanceSummaryText && (
                      <p className="text-xs text-muted-foreground">{report.vigilanceSummaryText}</p>
                    )}
                  </div>
                )}

                {report.memoryDetail && (
                  <div className="bg-background border border-border rounded-2xl p-4">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-sm font-medium text-muted-foreground">Память MoCA (свободное воспроизведение)</p>
                      <p className="text-xl font-bold text-primary">{report.memoryDetail.freeRecallCount} / 5</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="bg-secondary rounded-lg py-2">
                        <p className="text-lg font-bold text-primary">{report.memoryDetail.freeRecallCount}</p>
                        <p className="text-[10px] text-muted-foreground">свободно</p>
                      </div>
                      <div className="bg-secondary rounded-lg py-2">
                        <p className="text-lg font-bold text-primary">{report.memoryDetail.hintCount}</p>
                        <p className="text-[10px] text-muted-foreground">с подсказкой</p>
                      </div>
                      <div className="bg-secondary rounded-lg py-2">
                        <p className="text-lg font-bold text-primary">{report.memoryDetail.choiceCount}</p>
                        <p className="text-[10px] text-muted-foreground">с выбором</p>
                      </div>
                      <div className="bg-secondary rounded-lg py-2">
                        <p className="text-lg font-bold text-primary">{report.memoryDetail.failCount}</p>
                        <p className="text-[10px] text-muted-foreground">не вспомнил</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground/70 mt-2 italic">
                      Официальный балл MoCA учитывает только «свободно». MoCA-MIS ниже — отдельная
                      исследовательская метрика с весами подсказок, не заменяет официальный балл.
                    </p>
                  </div>
                )}

                <div className="bg-background border border-border rounded-2xl p-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-sm font-medium text-muted-foreground">Индекс памяти (MIS) — маркер БА</p>
                    <p className={`text-xl font-bold ${(report.mis || 0) < 5 ? 'text-red-500' : (report.mis || 0) >= 8 ? 'text-green-500' : 'text-yellow-500'}`}>
                      {report.mis || 0} / 15
                    </p>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2.5">
                    <div 
                      className={`${(report.mis || 0) < 5 ? 'bg-red-500' : (report.mis || 0) >= 8 ? 'bg-green-500' : 'bg-yellow-500'} h-2.5 rounded-full transition-all duration-500`} 
                      style={{ width: `${((report.mis || 0) / 15) * 100}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {report.misPrognosis || 'Данные по прогнозу отсутствуют'}
                  </p>
                </div>

                   <div className="bg-background border border-border rounded-2xl p-4">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-sm font-medium text-muted-foreground">VLOM-индекс (Язык / Память + Ориентация)</p>
                    <p className="text-xl font-bold text-primary">{report.isVlomValid ? report.vlomRatio : '—'}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {report.isVlomValid ? (
                      report.vlomRatio > 0.8 ? 'Высокий показатель (маркер Болезни Альцгеймера)' : 
                      report.vlomRatio < 0.5 ? 'Низкий показатель (маркер bvFTD)' : 
                      'Промежуточный профиль (равномерное снижение)'
                    ) : 'Недостаточно данных (Память и Ориентация = 0)'}
                  </p>
                  <p className="text-[10px] text-muted-foreground/70 mt-1 italic">*Адаптация для MoCA, пороги эмпирические</p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {report.clinicalScenarios && report.clinicalScenarios.length > 0 ? (
                  <div className={`p-4 rounded-2xl border-2 ${report.clinicalScenarios[0].includes('Сценарий А') ? 'bg-yellow-50 border-yellow-400 text-yellow-800' : report.clinicalScenarios[0].includes('Сценарий Б') ? 'bg-red-50 border-red-400 text-red-800' : 'bg-orange-50 border-orange-400 text-orange-800'}`}>
                    <p className="text-sm font-semibold">{report.clinicalScenarios.join(' ')}</p>
                  </div>
                ) : (
                  <div className="p-4 rounded-2xl border-2 border-green-400 bg-green-50 text-green-800">
                    <p className="text-sm font-semibold">Патологических сценариев не выявлено.</p>
                  </div>
                )}

                                 {report.differentialDiagnosis && report.differentialDiagnosis.length > 0 && (
                  <div className="bg-background border border-border rounded-2xl p-4">
                    <p className="text-sm font-semibold text-primary mb-1">Гипотезы по профилю MoCA</p>
                    <p className="text-[11px] text-muted-foreground mb-3">Черновик для врача — не диагноз.</p>
                    {report.differentialDiagnosis.map((d, idx) => (
                      <div key={idx} className="mb-3 last:mb-0 pb-3 border-b border-border last:border-b-0 last:pb-0">
                        <div className="flex items-center gap-2">
                           <p className="text-sm font-bold text-accent">Данные поддерживают: {d.diagnosis}</p>
                           {d.confidence === 'low' && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-bold">Низкая уверенность</span>}
                           {d.confidence === 'medium' && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full font-bold">Средняя уверенность</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{d.reason}</p>
                        {d.recommendation && (
                          <div className="mt-2 flex items-start gap-1.5 bg-secondary p-2 rounded-lg">
                            <ClipboardList className="size-3.5 text-primary shrink-0 mt-0.5" />
                        <p className="text-xs font-medium text-primary">{d.recommendation}</p>
                      </div>
                    )}
                  </div>
                ))}
                 <p className="text-[10px] text-muted-foreground/70 mt-3 pt-3 border-t border-border italic">
                      Гипотеза построена по профилю одного скрининга и не является диагнозом. Смешанный
                      нейродегенеративный или сосудистый процесс не может быть исключён по единственному
                      обследованию — требуется подтверждение анамнезом, нейровизуализацией и полной
                      нейропсихологической оценкой.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {report.subScores && report.subScores.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                 <div className="bg-secondary/50 rounded-3xl p-6 shadow-xl border border-border">
                <h2 className="text-xl font-semibold text-primary mb-4">Профиль когнитивных функций (%)</h2>
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={report.subScores.map(s => ({
                      domain: s.domain.split(' ')[0], 
                      fullMark: 100,
                      value: Math.round((s.score / s.max) * 100)
                    }))}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="domain" tick={{ fill: '#6b7280', fontSize: 14, fontWeight: 'bold' }} />
                      <PolarRadiusAxis domain={[0, 100]} tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={true} />
                      <Radar name="Score" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-secondary/50 rounded-3xl p-6 shadow-xl border border-border">
                <h2 className="text-xl font-semibold text-primary mb-4">Детализация по доменам</h2>
                <div className="flex flex-col gap-3">
                  {report.subScores.map((sub, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 bg-background rounded-xl border border-border">
                      <span className="font-medium text-primary">{sub.domain}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-secondary rounded-full h-2.5 overflow-hidden">
                          <div className="bg-accent h-2.5 rounded-full" style={{ width: `${(sub.score / sub.max) * 100}%` }}></div>
                        </div>
                        <span className="text-sm font-semibold text-muted-foreground w-10 text-right">{sub.score}/{sub.max}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="bg-secondary/50 rounded-3xl p-6 shadow-xl border border-border">
            <h2 className="text-xl font-semibold text-primary mb-4">Ответы пациента и клинические примечания</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left border-collapse">
                <thead>
                  <tr className="border-b-2 border-border text-muted-foreground">
                    <th className="py-3 px-2 w-12">№</th>
                    <th className="py-3 px-2">Вопрос</th>
                    <th className="py-3 px-2 min-w-[150px]">Ответ пациента</th>
                    <th className="py-3 px-2 min-w-[100px]">Эталон</th>
                    <th className="py-3 px-2 w-32 text-center">Оценка</th>
                    <th className="py-3 px-2 min-w-[200px]">Примечания врача</th>
                  </tr>
                </thead>
                <tbody>
                    {report.detailedAnswers.filter(Boolean).map((item) => (
                    <tr key={item.id} className={`border-b border-border ${getRowColor(item.isCorrect, item.needsManualScore, item.points)}`}>
                      <td className="py-2 px-2 font-bold text-muted-foreground">{item.id}</td>
                      <td className="py-2 px-2 text-primary text-xs">
                        {item.text}
                        {item.repeats > 0 && (
                          <span
                            className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] text-muted-foreground align-middle"
                            title={`Инструкция повторена ${item.repeats} раз(а)`}
                          >
                            <RotateCcw className="size-2.5" />×{item.repeats}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-primary text-xs">
                        {item.image ? (
                          <img src={item.image} alt="Рисунок пациента" className="w-24 h-24 object-contain border border-border rounded bg-white" />
                        ) : (
                          item.patientAnswer
                        )}
                      </td>
                      <td className="py-2 px-2 text-muted-foreground text-xs">{Array.isArray(item.correctAnswer) ? item.correctAnswer.join(', ') : item.correctAnswer}</td>
                        <td className="py-2 px-2 text-center text-xs font-semibold">
                        {item.needsManualScore ? (
                          item.id === '11a' ? (
                            <DrawingChecklistPentagon 
                              itemId={item.id} 
                              onSubmit={(itemId, checklist) => handleDrawingChecklistSubmit(itemId, checklist)}
                            />
                          ) : (
                            <DrawingChecklist 
                              itemId={item.id} 
                              onSubmit={(itemId, checklist) => handleDrawingChecklistSubmit(itemId, checklist)}
                            />
                          )
                        ) : (
                          getStatusText(item)
                        )}
                      </td>
                     <td className="py-2 px-2">
                    <input 
                     type="text" 
                     defaultValue={item.note || ''} 
                    placeholder="Заметка..."
                    className="w-full p-1 bg-background rounded border border-border text-xs focus:outline-none focus:border-accent"
                     />
                    </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-center pb-8">
            <button 
              onClick={() => navigate('/patient')} 
              className="flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              <Home className="size-6" /> Завершить и вернуться
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Запуск ScaleRunner (использует scaleData, загруженный из Supabase)
  return (
    <ScaleRunner 
      key={scaleId} 
      scale={scaleData} 
      onComplete={handleComplete} 
      taskNumber={1} 
      showControls={scaleId === 'nihss'} 
    />
  );
}